import { Company, PromotionalCode } from '@leadgen/models/client'
import promotionalCodeService from '../../services/promotionalCodeService/promotionalCodeService'
import promotionalCodeRedemptionService from '../../services/promotionalCodeRedemptionService/promotionalCodeRedemptionService'
import validators from '../validators'

export type PromotionalCodeValidationResult =
  | {
      valid: true
      promotionalCode: PromotionalCode
    }
  | {
      valid: false
      reason:
        | 'not_found'
        | 'expired'
        | 'already_redeemed'
        | 'invalid_mode'
        | 'invalid_type'
        | 'not_first_purchase'
    }

class PromoCodeForm {
  submitPromoCodeValidation = async ({
    promotionalCode,
    company,
  }: {
    promotionalCode: string
    company: Company
  }): Promise<PromotionalCodeValidationResult> => {
    const trimmedPromotionalCode = promotionalCode.trim()
    if (!trimmedPromotionalCode) {
      return {
        valid: false,
        reason: 'not_found',
      }
    }
    const promotionalCodeRecord = await promotionalCodeService.fetchPromotionalCodeById({
      promotionalCodeId: trimmedPromotionalCode,
    })
    const promotionalCodeRedemption =
      await promotionalCodeRedemptionService.fetchPromotionalCodeRedemption({
        companyId: company.id,
        promotionalCodeId: trimmedPromotionalCode,
      })
    if (!promotionalCodeRecord) {
      return {
        valid: false,
        reason: 'not_found',
      }
    }
    if (new Date(promotionalCodeRecord.expiry).getTime() < Date.now()) {
      return {
        valid: false,
        reason: 'expired',
      }
    }
    if (promotionalCodeRecord.mode !== 'first-purchase') {
      return {
        valid: false,
        reason: 'invalid_mode',
      }
    }
    if (promotionalCodeRecord.type !== 'percentage-additional-credits') {
      return {
        valid: false,
        reason: 'invalid_type',
      }
    }
    if (
      promotionalCodeRecord.mode === 'first-purchase' &&
      company.creditsPurchasedYearMonth !== null
    ) {
      return {
        valid: false,
        reason: 'not_first_purchase',
      }
    }
    if (promotionalCodeRedemption?.charges?.length) {
      return {
        valid: false,
        reason: 'already_redeemed',
      }
    }
    return {
      valid: true,
      promotionalCode: promotionalCodeRecord,
    }
  }
  validatePromoCodeField = validators.validateLengthMax({
    max: 64,
    errorMessage: 'This promotional code is too long.',
  })
  getValidationErrorMessage = (
    result: Exclude<PromotionalCodeValidationResult, { valid: true }>['reason'],
  ) => {
    switch (result) {
      case 'not_found':
        return 'This promotional code was not found.'
      case 'expired':
        return 'This promotional code has expired.'
      case 'already_redeemed':
        return 'This promotional code has already been used.'
      case 'invalid_mode':
      case 'invalid_type':
        return 'This promotional code is not valid for this subscription.'
      case 'not_first_purchase':
        return 'This promotional code only applies to your first purchase.'
      default:
        return 'This promotional code is not valid.'
    }
  }
}

export default new PromoCodeForm()
